import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

import IconAlignTop from '@/assets/icons/icon-vertical-align-top.svg?react';
import IconAlignTopFilled from '@/assets/icons/icon-vertical-align-top-filled.svg?react';
import IconAlignMiddle from '@/assets/icons/icon-vertical-align-center.svg?react';
import IconAlignMiddleFilled from '@/assets/icons/icon-vertical-align-center-filled.svg?react';
import IconAlignBottom from '@/assets/icons/icon-vertical-align-bottom.svg?react';
import IconAlignBottomFilled from '@/assets/icons/icon-vertical-align-bottom-filled.svg?react';

interface ContentAlignmentVerticalProps {
  text?: string;
  align?: string;
  onClick?: (size: string) => void;
  className?: string;
}

const aligns: Array<'top' | 'center' | 'bottom'> = ['top', 'center', 'bottom'];

export const ContentAlignmentVertical: React.FC<ContentAlignmentVerticalProps> = ({
  align = 'top',
  onClick,
  className,
  text = 'Align',
}) => {
  const [selectedSize, setSelectedSize] = useState<string>(align);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, top: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    top: null,
    center: null,
    bottom: null,
  });

  useEffect(() => {
    if (align !== selectedSize) {
      setSelectedSize(align);
    }
  }, [align]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        top: activeEl.offsetLeft,
      });
    }
  }, [selectedSize]);

  const handleClick = (newSize: string) => {
    setIsAnimated(true);
    setSelectedSize(newSize);
    if (onClick) onClick(newSize);
  };

  return (
    <div className={clsx('flex items-center gap-4', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <div className="relative ml-auto flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={clsx(
            'absolute top-0 z-20 h-full rounded-md bg-[#fff] outline outline-offset-[-1px] outline-[#e9ecf0]',
            isAnimated ? 'transition-all duration-300' : '',
          )}
          style={{
            width: highlightStyle.width,
            transform: `translateX(${highlightStyle.top}px)`,
          }}
        />

        {aligns.map((sz) => {
          let Icon;
          if (sz === 'top') {
            Icon = selectedSize === sz ? IconAlignTopFilled : IconAlignTop;
          } else if (sz === 'center') {
            Icon = selectedSize === sz ? IconAlignMiddleFilled : IconAlignMiddle;
          } else {
            Icon = selectedSize === sz ? IconAlignBottomFilled : IconAlignBottom;
          }
          const iconClass = selectedSize === sz ? 'text-indigo-500' : 'text-neutral-400';
          return (
            <button
              key={sz}
              ref={(el) => {
                optionRefs.current[sz] = el;
              }}
              onClick={() => handleClick(sz)}
              className="relative z-20 flex h-8 w-8 cursor-pointer items-center justify-center"
              type="button"
            >
              <Icon className={`h-4.5 w-4.5 ${iconClass}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};
